import { ReactElement, useCallback, useMemo, useState } from 'react';
import { Checkbox } from '@Components/FormCheck';

interface UseVisibleRowSelectionArgs<Row, Id> {
  rows: Row[];
  getId: (row: Row) => Id;
  selected: Set<Id>;
  onSelectedChange: (selected: Set<Id>) => void;
  selectAllLabel?: string;
}

interface VisibleRowSelection<Row, Id> {
  toggleSelected: (id: Id) => void;
  // Pass to BPMTable's onFilteredDataChange.
  onFilteredDataChange: (rows: Row[]) => void;
  // Pass to BPMTable's headersRenderer: column 0 becomes a select-all checkbox over the visible
  // rows, and every other column keeps its string label.
  headersRenderer: (cell: { data: unknown; columnIndex: number }) => ReactElement;
}

// Multi-select for a BPMTable whose first column is a row checkbox. Select-all only covers rows
// passing the filter bar: with none of them selected it selects them all, otherwise it clears
// them. Rows hidden by the filter are never touched.
export const useVisibleRowSelection = <Row, Id>({
  rows,
  getId,
  selected,
  onSelectedChange,
  selectAllLabel = 'Select all visible'
}: UseVisibleRowSelectionArgs<Row, Id>): VisibleRowSelection<Row, Id> => {
  // Rows passing the filter bar; null until the filter bar first reports, meaning all rows.
  const [filteredRows, setFilteredRows] = useState<Row[] | null>(null);
  const visibleIds = useMemo(() => (filteredRows ?? rows).map(getId), [filteredRows, rows, getId]);
  const visibleSelectedCount = visibleIds.filter(id => selected.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;

  const toggleSelected = useCallback(
    (id: Id) => {
      const next = new Set(selected);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onSelectedChange(next);
    },
    [selected, onSelectedChange]
  );

  const toggleSelectAllVisible = useCallback(() => {
    const next = new Set(selected);
    if (visibleSelectedCount === 0) {
      visibleIds.forEach(id => next.add(id));
    } else {
      visibleIds.forEach(id => next.delete(id));
    }
    onSelectedChange(next);
  }, [selected, visibleIds, visibleSelectedCount, onSelectedChange]);

  // useSortHeader hands the string label through as `data`, so it's accepted as unknown.
  const headersRenderer = useCallback(
    ({ data, columnIndex }: { data: unknown; columnIndex: number }): ReactElement => {
      if (columnIndex === 0) {
        return (
          <Checkbox
            checked={allVisibleSelected}
            indeterminate={visibleSelectedCount > 0 && !allVisibleSelected}
            disabled={visibleIds.length === 0}
            onChange={toggleSelectAllVisible}
            onClick={e => e.stopPropagation()}
            aria-label={selectAllLabel}
          />
        );
      }
      return <>{typeof data === 'string' ? data : null}</>;
    },
    [
      allVisibleSelected,
      visibleSelectedCount,
      visibleIds.length,
      toggleSelectAllVisible,
      selectAllLabel
    ]
  );

  return { toggleSelected, onFilteredDataChange: setFilteredRows, headersRenderer };
};
